import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

// Modal Component for Create/Edit
function LegalModal({ contenido, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    tipo: contenido?.tipo || 'privacidad',
    titulo: contenido?.titulo || '',
    contenido: contenido?.contenido || '',
    activo: contenido?.activo ?? true,
    publicado: contenido?.publicado ?? true
  })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      if (contenido) {
        // Editar - incrementar versión
        const { data: currentData, error: fetchError } = await supabase
          .from('contenido_legal')
          .select('version')
          .eq('id', contenido.id)
          .single()

        if (fetchError) throw fetchError

        const { error } = await supabase
          .from('contenido_legal')
          .update({
            ...formData,
            version: (currentData.version || 0) + 1
          })
          .eq('id', contenido.id)

        if (error) throw error
      } else {
        // Crear nuevo
        const { error } = await supabase
          .from('contenido_legal')
          .insert([formData])

        if (error) throw error
      }

      alert('Contenido legal guardado exitosamente')
      onSuccess()
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar el contenido legal')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg w-full my-8" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '896px' }}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {contenido ? 'Editar' : 'Nuevo'} Contenido Legal
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Alert Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Estado de Publicación:</strong> Los contenidos en "Borrador" solo son visibles en este panel. 
              Marca como "Publicado" y "Activo" cuando esté listo para aparecer en el sitio web público.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Contenido *</label>
              <select
                required
                value={formData.tipo}
                onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="privacidad">🔒 POLÍTICAS DE PRIVACIDAD</option>
                <option value="terminos">📜 TÉRMINOS Y CONDICIONES</option>
                <option value="cookies">🍪 POLÍTICAS DE COOKIES</option>
              </select>
            </div>

            {/* Estados */}
            <div className="flex gap-4 items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.publicado}
                  onChange={(e) => setFormData({...formData, publicado: e.target.checked})}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium">
                  {formData.publicado ? '✅ Publicar' : '📝 Borrador'}
                </span>
              </label>

              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.activo}
                  onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="ml-2 text-sm font-medium">
                  {formData.activo ? '🟢 Activo' : '⚫ Inactivo'}
                </span>
              </label>
            </div>

            {/* Título */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Título *</label>
              <input
                type="text"
                required
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: Política de Privacidad de Plaza Aurora"
              />
            </div>

            {/* Contenido */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Contenido *</label>
              <textarea
                required
                rows={20}
                value={formData.contenido}
                onChange={(e) => setFormData({...formData, contenido: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                placeholder="Escribe el contenido legal aquí... Usa saltos de línea para separar párrafos."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.contenido.length} caracteres • {formData.contenido.split('\n').length} líneas
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Guardando...' : (contenido ? 'Actualizar' : 'Crear Contenido')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Main Component
export default function Legal() {
  const [contenidos, setContenidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingContenido, setEditingContenido] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTipo, setSelectedTipo] = useState('')
  const [selectedPublicado, setSelectedPublicado] = useState('')

  useEffect(() => {
    fetchContenidos()
  }, [])

  const fetchContenidos = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('contenido_legal')
        .select('*')
        .order('tipo')
        .order('created_at', { ascending: false })

      if (error) throw error
      setContenidos(data || [])
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingContenido(null)
    setShowModal(true)
  }

  const handleEdit = (contenido) => {
    setEditingContenido(contenido)
    setShowModal(true)
  }

  const handleDelete = async (id, titulo) => {
    if (!confirm(`¿Estás seguro de eliminar "${titulo}"?`)) return

    try {
      const { error } = await supabase
        .from('contenido_legal')
        .delete()
        .eq('id', id)

      if (error) throw error

      alert('Contenido eliminado exitosamente')
      fetchContenidos()
    } catch (error) {
      console.error('Error:', error)
      alert('Error al eliminar el contenido')
    }
  }

  const togglePublicado = async (id, currentState) => {
    const { error } = await supabase
      .from('contenido_legal')
      .update({ publicado: !currentState })
      .eq('id', id)

    if (!error) {
      fetchContenidos()
    }
  }

  const toggleActivo = async (id, currentState) => {
    const { error } = await supabase
      .from('contenido_legal')
      .update({ activo: !currentState })
      .eq('id', id)

    if (!error) {
      fetchContenidos()
    }
  }

  const getTipoLabel = (tipo) => {
    const labels = {
      'privacidad': 'Políticas de Privacidad',
      'terminos': 'Términos y Condiciones',
      'cookies': 'Políticas de Cookies'
    }
    return labels[tipo] || tipo
  }

  const getTipoIcon = (tipo) => {
    const icons = {
      'privacidad': '🔒',
      'terminos': '📜',
      'cookies': '🍪'
    }
    return icons[tipo] || '📄'
  }

  // Filtros
  const filteredContenidos = contenidos.filter(contenido => {
    const matchesSearch = contenido.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contenido.contenido.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTipo = !selectedTipo || contenido.tipo === selectedTipo
    const matchesPublicado = !selectedPublicado || 
                            (selectedPublicado === 'publicado' ? contenido.publicado : !contenido.publicado)
    return matchesSearch && matchesTipo && matchesPublicado
  })

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando contenido legal...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">LEGAL</h1>
          <p className="text-gray-600 mt-1">
            Administra los textos legales que se muestran en el sitio web
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          <span>Nuevo Contenido</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Buscar */}
          <input
            type="text"
            placeholder="Buscar por título o contenido..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          />

          {/* Filtro por tipo */}
          <select
            value={selectedTipo}
            onChange={(e) => setSelectedTipo(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">Todos los tipos</option>
            <option value="privacidad">🔒 Privacidad</option>
            <option value="terminos">📜 Términos</option>
            <option value="cookies">🍪 Cookies</option>
          </select>

          {/* Filtro por publicado */}
          <select
            value={selectedPublicado}
            onChange={(e) => setSelectedPublicado(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">Todos los estados</option>
            <option value="publicado">✅ Publicados</option>
            <option value="borrador">📝 Borradores</option>
          </select>
        </div>

        <div className="mt-3 text-sm text-gray-600">
          Mostrando {filteredContenidos.length} de {contenidos.length} contenidos
        </div>
      </div>

      {/* Lista de Contenidos */}
      <div className="space-y-4">
        {filteredContenidos.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <p className="text-gray-500">No se encontraron contenidos legales</p>
          </div>
        ) : (
          filteredContenidos.map((contenido) => (
            <div key={contenido.id} className="bg-white rounded-lg shadow">
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-2xl">{getTipoIcon(contenido.tipo)}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg">{contenido.titulo}</h3>
                      <span className="text-xs text-gray-500">v{contenido.version}</span>
                    </div>
                    <p className="text-sm text-gray-600">{getTipoLabel(contenido.tipo)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Toggle Publicado */}
                  <button
                    onClick={() => togglePublicado(contenido.id, contenido.publicado)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      contenido.publicado
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {contenido.publicado ? '✅ Publicado' : '📝 Borrador'}
                  </button>

                  {/* Toggle Activo */}
                  <button
                    onClick={() => toggleActivo(contenido.id, contenido.activo)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      contenido.activo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {contenido.activo ? '🟢 Activo' : '⚫ Inactivo'}
                  </button>

                  {/* Botón Editar */}
                  <button
                    onClick={() => handleEdit(contenido)}
                    className="px-3 py-1 bg-primary text-white rounded hover:bg-blue-700 text-sm"
                  >
                    ✏️ Editar
                  </button>

                  {/* Botón Eliminar */}
                  <button
                    onClick={() => handleDelete(contenido.id, contenido.titulo)}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Preview del Contenido */}
              <div className="p-4">
                <div className="bg-gray-50 rounded p-3 max-h-32 overflow-hidden">
                  <p className="text-sm text-gray-700 whitespace-pre-line line-clamp-3">
                    {contenido.contenido}
                  </p>
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                  <span>📄 {contenido.contenido.length} caracteres</span>
                  <span>•</span>
                  <span>📅 {new Date(contenido.updated_at).toLocaleDateString('es-PE')}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <LegalModal
          contenido={editingContenido}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false)
            fetchContenidos()
          }}
        />
      )}
    </div>
  )
}

